// src/common/infrastructure/config/fullstoryInit.ts
/**
 * FullStory Integration for React + Vite
 *
 * Uses @fullstory/browser npm package for modern integration
 * - User identification on login
 * - SPA route tracking (automatic page view tracking)
 * - Custom event tracking support
 */

import { init, FullStory } from '@fullstory/browser';
import { LoggerService } from '../services/LoggerService';
import { AuthenticatedUser } from '@/auth/application/AuthContext';
const logger = LoggerService.create('FullStory');

export interface FullStoryUser {
  uid: string;
  username?: string;
  displayName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  accountId: string;
  accountName?: string;
  [key: string]: unknown;
}

let isInitialized = false;
const ORG_ID = import.meta.env.VITE_FULLSTORY_ORG ?? 'o-247J25-na1';

/**
 * Initializes FullStory with organization ID
 * Should be called once at application startup
 */
const initializeFullStorySDK = (): void => {
  if (isInitialized) {
    return;
  }

  try {
    init({ orgId: ORG_ID });
    isInitialized = true;
    logger.debug('FullStory SDK initialized successfully');
  } catch (error) {
    logger.error('Failed to initialize FullStory SDK:', error);
    throw error;
  }
};

/**
 * Sets up FullStory analytics with user identification
 * @param user - User data for FullStory identification
 * @param _orgId - FullStory organization ID (optional, kept for API compatibility)
 * @returns Promise that resolves when initialization is complete
 */
export const setupFullStory = async (user: AuthenticatedUser, _orgId?: string): Promise<void> => {
  if (!user?.userId || !user.dbName) {
    return;
  }

  try {
    // Initialize SDK if not already initialized
    if (!isInitialized) {
      initializeFullStorySDK();
    }

    const fullstoryObject = {
      uid: `userid:${user.userId}-${user.dbName}-${user.fullName}`,
      properties: {
        displayName: user.fullName,
        email: user.userEmail,
        ...user
      }
    };
    // Set user variables using FullStory API
    FullStory('setIdentity', fullstoryObject);
    logger.debug('FullStory user identification set successfully');
  } catch (error) {
    logger.error('Failed to setup FullStory:', error);
    throw error;
  }
};

/**
 * Tracks page navigation for SPAs
 * Call this whenever the route changes to update FullStory with the current page
 * @param pathname - The current page pathname (e.g., window.location.pathname)
 * @param queryParams - Optional query parameters to include in page tracking (module, submodule, etc.)
 */
export const trackPageView = (pathname: string, queryParams?: { module?: string | null; submodule?: string | null }): void => {
  try {
    // Build pageName with pathname and relevant query params
    let pageName = pathname;

    // Add module and submodule to pageName if they exist
    const params: string[] = [];
    if (queryParams?.module) {
      params.push(`module=${queryParams.module}`);
    }
    if (queryParams?.submodule) {
      params.push(`submodule=${queryParams.submodule}`);
    }

    if (params.length > 0) {
      pageName = `${pathname}?${params.join('&')}`;
    }

    // Set page properties using FullStory API
    onFullStoryReady(() => {
      FullStory('setPage', { pageName });
    });
    logger.debug('FullStory page view tracked successfully', { pageName });
  } catch (error) {
    logger.warn('Failed to track FullStory page view:', error);
  }
};

/**
 * Tracks custom events in FullStory
 * @param eventName - Name of the event
 * @param properties - Optional event properties
 */
export const trackEvent = (eventName: string, properties?: Record<string, unknown>): void => {
  try {
    // Track event using FullStory API
    FullStory('trackEvent', {
      name: eventName,
      properties: properties ?? {}
    });
    logger.debug('FullStory event tracked successfully', { eventName, properties });
  } catch (error) {
    logger.warn('Failed to track FullStory event:', error);
  }
};

/**
 * Gets the current FullStory session URL
 * @param _atCurrentTime - If true, returns URL to current moment in session; if false, returns URL to beginning of session (not supported in v2 API)
 * @returns Session URL string or null if session is not available
 */
export const getCurrentSessionURL = (_atCurrentTime: boolean = false): string | null => {
  try {
    // Get session URL using FullStory API
    // Note: atCurrentTime parameter is not supported in v2 API, always returns URL to beginning of session
    const sessionUrl = FullStory('getSession', { format: 'url' }) as string | null | undefined;
    if (sessionUrl) {
      logger.debug('FullStory session URL retrieved successfully');
      return sessionUrl;
    }
    return null;
  } catch (error) {
    logger.warn('Failed to get FullStory session URL:', error);
    return null;
  }
};

/**
 * Checks if FullStory is active and ready
 * @returns true if FullStory is initialized, false otherwise
 */
export const isFullStoryReady = (): boolean => {
  return isInitialized;
};

/**
 * Waits for FullStory to be ready and then executes a callback
 * @param callback - Function to execute when FullStory is ready
 */
export const onFullStoryReady = (callback: () => void): void => {
  if (isInitialized) {
    // FullStory is already ready
    callback();
    return;
  }

  // Initialize and then call callback
  try {
    initializeFullStorySDK();
    callback();
  } catch (error) {
    logger.warn('Failed to initialize FullStory for ready callback:', error);
    // Still call callback to avoid blocking
    callback();
  }
};

/**
 * Debug helper to check FullStory status and get diagnostic information
 * Useful for testing and troubleshooting
 * @returns Object with FullStory status information
 */
export const getFullStoryDebugInfo = (): {
  isReady: boolean;
  isInitialized: boolean;
  orgId: string;
  sessionUrl: string | null;
  userInfo: Record<string, unknown> | null;
} => {
  try {
    const sessionUrl = getCurrentSessionURL();
    // Try to get user info from FullStory
    let userInfo: Record<string, unknown> | null = null;
    try {
      // FullStory doesn't expose a direct API to get current user info
      // But we can check if the session URL exists as an indicator
      userInfo = sessionUrl ? { sessionActive: true } : null;
      logger.debug('FullStory user info retrieved successfully', { userInfo });
    } catch (error) {
      // Ignore errors when trying to get user info
      logger.warn('Failed to get FullStory user info:', error);
      userInfo = null;
    }

    return {
      isReady: isInitialized,
      isInitialized,
      orgId: ORG_ID,
      sessionUrl,
      userInfo
    };
  } catch (error) {
    logger.warn('Failed to get FullStory debug info:', error);
    return {
      isReady: false,
      isInitialized: false,
      orgId: ORG_ID,
      sessionUrl: null,
      userInfo: null
    };
  }
};
